import { useRef, useState } from 'react'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, Divider, Stack, Typography, } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import { useGetLojaAtualQuery, type LojaAtualResponse } from '../api'
import { ehDispositivoFixo, rotuloTipoPedido } from '../lib/canal'
import { abrirConfigDispositivo } from '../lib/tablet-eventos'
import { DialogTitle } from './dialog-componentes'

const VERSAO = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : ''

function DialogSobre({ open: aberto, onClose, onFechado, loja, onAbrirConfiguracoes, }: { open: boolean; onClose: () => void; onFechado: () => void; loja: LojaAtualResponse; onAbrirConfiguracoes: () => void }): JSX.Element {
  const ehAparelhoDaLoja = ehDispositivoFixo(loja.identificador)

  return (
    <Dialog open={aberto} onClose={onClose} fullWidth maxWidth="xs" TransitionProps={{ onExited: onFechado }}>
      <DialogTitle onClose={onClose}>
        Sobre o Sischef Cardápio {' '}

        <Typography variant="caption" color="text.secondary" textAlign="center">
          - {VERSAO ? `Versão ${VERSAO}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack spacing={1} width="fit-content">
            <Typography variant="body2" fontWeight="bold">
              Modo atual
            </Typography>

            <Chip
              label={rotuloTipoPedido(loja.identificador.tipo)}
              color="primary"
              size="small"
            />
          </Stack>

          <Stack spacing={1} width="fit-content">
            <Typography variant="body2" fontWeight="bold">
              Loja
            </Typography>

            <Chip
              label={loja.configuracao.nome}
              color="primary"
              size="small"
            />
          </Stack>

          <Divider textAlign='center'>
            <Typography>
              Sischef
            </Typography>
          </Divider>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Cardápio digital do <strong>Sistema Sischef</strong>, com pedidos de delivery, mesa, comanda, totem e tablet totalmente integrados ao sistema de gestão da loja.

          </Typography>

          <Typography variant="body2" color="primary" textAlign="center">
            https://sischef.com
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', p: 2 }}>
        {ehAparelhoDaLoja && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={onAbrirConfiguracoes}
            sx={{ textTransform: 'none', }}
          >
            Configurações do aparelho
          </Button>
        )}
      </DialogActions>
    </Dialog >
  )
}

export function SobreCardapio({ posicao = 'flutuante', }: { posicao?: 'flutuante' | 'embutido' | 'rodape' } = {}): JSX.Element | null {
  const { data: loja } = useGetLojaAtualQuery()
  const [aberto, setAberto] = useState(false)
  const configAoFechar = useRef(false)

  if (!loja) return null

  const noRodape = posicao === 'rodape'

  function handleOpenSobreDialog(): void {
    setAberto(true)
  }

  function handleCloseSobreDialog(): void {
    setAberto(false)
  }

  // Abrindo junto, o "Sobre" devolve o foco ao botão dele no meio da abertura e o teclado do PIN fecha.
  function abrirConfiguracoes(): void {
    configAoFechar.current = true
    handleCloseSobreDialog()
  }

  function aoFecharSobre(): void {
    if (!configAoFechar.current) return
    configAoFechar.current = false
    abrirConfigDispositivo()
  }

  return (
    <>
      <Button
        size={noRodape ? 'small' : 'small'}
        color="inherit"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={handleOpenSobreDialog}
        sx={{
          textTransform: 'none',
          flexShrink: 0,
          fontWeight: 400,
          minWidth: 0,
          px: 1,
          color: noRodape ? 'inherit' : 'text.secondary',
          opacity: noRodape ? 0.85 : 1,
          transition: 'opacity .2s, color .2s',
          '& .MuiButton-startIcon': {
            mr: 0.75,
            ml: 0,
          },
          '&:hover, &:focus-visible': {
            opacity: 1,
            color: noRodape ? 'inherit' : 'text.primary',
          },
          ...(posicao === 'flutuante' && {
            position: 'fixed',
            left: 'calc(8px + env(safe-area-inset-left))',
            bottom: 'calc(8px + env(safe-area-inset-bottom))',
            zIndex: (t: Theme) => t.zIndex.fab,
          }),
        }}
      >
        Sobre
      </Button>

      <DialogSobre
        open={aberto}
        onClose={handleCloseSobreDialog}
        onFechado={aoFecharSobre}
        loja={loja}
        onAbrirConfiguracoes={abrirConfiguracoes}
      />
    </>
  )
}

// Elemento pronto, de referência fixa: passado por prop a um componente memo (a coluna de categorias), não o faz redesenhar a cada render.
export const SOBRE_EMBUTIDO = <SobreCardapio posicao="embutido" />
